/** Auto Fill Points Program — configurable rules (admin can mirror in Firestore later). */

export const AUTO_FILL_MAX_LEVEL = 15;
export const AUTO_FILL_BASE_PAYMENT = 150;
/**
 * Each AFP row is funded at ₹150 in the matrix ledger.
 * New ₹699 memberships include AFP; legacy ₹499 members pay upgrade separately.
 */
export const AUTO_FILL_PROGRAM_ENTRY_AMOUNT = 150;
export const AUTO_FILL_ENTRY_AMOUNTS = [150];

/** Platform admin account — CO Points (fill #1) ledger balance in Auto Fill Money Flow. */
export const AUTO_FILL_COMPANY_EMAIL =
  process.env.REACT_APP_AUTO_FILL_COMPANY_EMAIL || "infinitywhale08@gmail.com";

/** Spreadsheet: 3 fills per level — #1 CO Points, #2 reserved, #3 + reserve → next level. */
export const AUTO_FILL_FILLS_PER_LEVEL = 3;

/**
 * Matrix placement by global AFP enrollment order (not referral tree):
 * AFP #2–4 → fill enrollee #1, #5–7 → #2, #8–10 → #3, etc.
 *
 * Per level at amount Auto Fill(L): 3 payments — #1 CO Points (withdrawal),
 * #2–#3 → upgrade pool; 2×Auto Fill(L) (e.g. ₹300 after 3×₹150 at L1) unlocks L+1.
 * Genesis (AFP #1 / Infinity Whale): that activation amount also credits Admin CO.
 */
export function uplineEnrollmentIndexForNewSeq(newEnrollmentSeq) {
  const n = Number(newEnrollmentSeq);
  if (n <= 1) return null;
  return Math.floor((n - 2) / 3) + 1;
}

/**
 * Matrix upline for an AFP that is receiving fills (same index as enrollee placement).
 * Genesis AFP #1 has no matrix upline (admin / ID 0 in public numbering).
 */
export function matrixUplineEnrollmentIndexForProgram(programEnrollmentSeq) {
  return uplineEnrollmentIndexForNewSeq(programEnrollmentSeq);
}

/** Public serial: genesis admin = 0, first member = 1, … (internal enrollmentSeq − 1). */
export function getPublicAfpSerial(enrollmentSeq) {
  const n = Number(enrollmentSeq);
  if (!n || n < 1) return 0;
  return n - 1;
}

/** @deprecated use upline current level at fill time */
export function tripletIndexForMatrixFillSeq(fillSeq) {
  const n = Number(fillSeq);
  if (n < 1) return null;
  return Math.floor((n - 1) / 3);
}

export const AUTO_FILL_UPLINE_ROTATION_SLOTS = 14;

/**
 * Global matrix fill routing — strict enrollment order (3-wide upline).
 * Fill #k is created when AFP enrollmentSeq (k + 1) joins.
 * Upline = same as enrollee placement: #2–4 → 1, #5–7 → 2, #8–10 → 3, …
 */
export function uplineEnrollmentIndexForMatrixFillSeq(fillSeq) {
  const n = Number(fillSeq);
  if (n < 1) return null;
  return uplineEnrollmentIndexForNewSeq(n + 1);
}

/**
 * Matrix level for a fill is the upline's **current** program level at fill time
 * (not derived from fillSeq). Kept for scripts that replay without live state.
 */
export function expectedMatrixLevelForFillSeq(fillSeq, uplineCurrentLevel = 1) {
  const uIdx = uplineEnrollmentIndexForMatrixFillSeq(fillSeq);
  if (uIdx == null) return null;
  void uIdx;
  return Math.max(1, Number(uplineCurrentLevel) || 1);
}

export function parseEnrollmentSeqFromProgramId(programPublicId) {
  const m = /^AFP-(\d+)$/.exec(String(programPublicId || ""));
  return m ? parseInt(m[1], 10) : null;
}

export function getLevelAmount(level) {
  const L = Number(level);
  if (L < 1 || L > AUTO_FILL_MAX_LEVEL) return 0;
  return AUTO_FILL_BASE_PAYMENT * Math.pow(2, L - 1);
}

export function getLevelPaymentTotals(level) {
  const amount = getLevelAmount(level);
  return {
    levelAmount: amount,
    threePayments: amount * 3,
    pointsWallet: amount,
    upgradePool: amount * 2,
  };
}

/** Direct referral counts required before entering this level (not L1–2). */
export function getRequiredDirectReferralsForLevel(level) {
  const L = Number(level);
  if (L <= 2) return 0;
  if (L >= 3 && L <= 5) return 0; // wallet/T&C gates instead
  if (L === 6) return 10;
  if (L === 7) return 10;
  if (L === 8) return 15;
  if (L === 9) return 20;
  if (L === 10) return 25;
  if (L === 11) return 30;
  if (L === 12) return 35;
  if (L === 13) return 40;
  if (L === 14) return 45;
  if (L === 15) return 50;
  return 0;
}

/** L9+: direct referrals must be Ultra members (current activation tier), not only AFP. */
export function withdrawalUsesUltraMemberQualification(level) {
  return Number(level) >= 9;
}

export function levelsRequiringUltraPlusWallets() {
  return [3, 4, 5];
}

/**
 * Capped team score for withdrawal: only users at `depth` (from you) with active AFP.
 * Each level-1 leg contributes at most `maxPerLeg` (50% of minTotal).
 */
export function getTeamAfpWithdrawalRules(level) {
  const L = Number(level);
  const teamByLevel = {
    7: { minTotal: 50, depth: 2, memberKind: "ultra" },
    8: { minTotal: 250, depth: 3, memberKind: "ultra" },
    9: { minTotal: 1000, depth: 4, memberKind: "ultra" },
    10: { minTotal: 3000, depth: 5, memberKind: "ultra" },
    11: { minTotal: 6000, depth: 6, memberKind: "ultra" },
    12: { minTotal: 12000, depth: 7, memberKind: "ultra" },
    13: { minTotal: 25000, depth: 8, memberKind: "ultra" },
    14: { minTotal: 50000, depth: 9, memberKind: "ultra" },
    15: { minTotal: 100000, depth: 10, memberKind: "ultra" },
  };
  const row = teamByLevel[L];
  if (!row) return null;
  return {
    minTotal: row.minTotal,
    maxPerLeg: Math.floor(row.minTotal / 2),
    depth: row.depth,
    memberKind: row.memberKind,
  };
}

export function getRequiredTeamAfpSizeForLevel(level) {
  const rules = getTeamAfpWithdrawalRules(level);
  return rules ? rules.minTotal : 0;
}

/**
 * Root (first) AFP only: an approved CO Points withdrawal at program level 2+
 * (₹300+ fill cycle) spawns the next global enrollment serial (new program at L1).
 * Level 1 withdrawals (₹150) do not spawn. Child AFPs do not spawn further.
 * Max 14 children — parent levels 2–15 can each drive one new serial (15−1).
 */
export const AUTO_FILL_MAX_CHILD_PROGRAMS_PER_ROOT = 14;
